import { useEffect, useState } from "react";
import { toast } from "sonner";
import type { Lang } from "../../lib/i18n";
import { pick } from "../../lib/i18n";
import {
  adminMe, adminChangePassword, adminListUsers, adminCreateUser, adminUpdateUser, adminDeleteUser,
  type AdminUser,
} from "../../lib/api";
import { logAudit } from "../lib/audit";

/** Admin Users tab — replaces the old single shared username/password with a
 *  real multi-admin list (name + email + password each), plus a "change my
 *  own password" card. Any signed-in admin can add or remove other admins. */
export function UsersTab({ lang }: { lang: Lang }) {
  const t = (ar: string, en: string) => pick(lang, ar, en);

  // --- Change my own password ---
  const [myEmail, setMyEmail] = useState("");
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [pwBusy, setPwBusy] = useState(false);
  const [pwMsg, setPwMsg] = useState<{ kind: "ok" | "err"; text: string } | null>(null);

  useEffect(() => { adminMe().then((r) => r.ok && r.email && setMyEmail(r.email)); }, []);

  async function submitPassword(e: React.FormEvent) {
    e.preventDefault();
    if (next !== confirmPwd) { setPwMsg({ kind: "err", text: t("كلمتا المرور غير متطابقتين", "Passwords do not match") }); return; }
    if (next.length < 8) { setPwMsg({ kind: "err", text: t("كلمة المرور يجب أن تكون 8 أحرف على الأقل", "Password must be at least 8 characters") }); return; }
    setPwBusy(true);
    const res = await adminChangePassword(current, next);
    setPwBusy(false);
    if (res.ok) {
      setPwMsg({ kind: "ok", text: t("تم تحديث كلمة المرور بنجاح", "Password updated successfully") });
      setCurrent(""); setNext(""); setConfirmPwd("");
      logAudit("credentials_changed", "Admin password changed");
    } else {
      setPwMsg({ kind: "err", text: res.error || t("كلمة المرور الحالية غير صحيحة", "Current password is incorrect") });
    }
  }

  // --- Admin users list ---
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [newName, setNewName] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [editEmail, setEditEmail] = useState("");

  async function loadUsers() {
    setLoading(true);
    const res = await adminListUsers();
    setLoading(false);
    if (res.ok) setUsers(res.users);
    else toast.error(t("تعذّر تحميل المستخدمين: ", "Failed to load users: ") + (res.error || ""));
  }
  useEffect(() => { loadUsers(); }, []);

  async function addUser(e: React.FormEvent) {
    e.preventDefault();
    if (!newName.trim() || !newEmail.trim() || newPassword.length < 8) {
      toast.error(t("يرجى تعبئة الاسم والبريد وكلمة مرور 8 أحرف على الأقل", "Please fill name, email, and a password of at least 8 characters"));
      return;
    }
    setAdding(true);
    const res = await adminCreateUser(newName.trim(), newEmail.trim(), newPassword);
    setAdding(false);
    if (res.ok) {
      toast.success(t("تمت إضافة المستخدم", "User added"));
      logAudit("config_imported", `Admin user added: ${newEmail.trim()}`);
      setNewName(""); setNewEmail(""); setNewPassword(""); setShowAdd(false);
      loadUsers();
    } else {
      toast.error(t("تعذّرت الإضافة: ", "Add failed: ") + (res.error || ""));
    }
  }

  function startEdit(u: AdminUser) {
    setEditingId(u.id); setEditName(u.name); setEditEmail(u.email);
  }
  async function saveEdit(id: string) {
    if (!editName.trim() || !editEmail.trim()) return;
    const res = await adminUpdateUser(id, editName.trim(), editEmail.trim());
    if (res.ok) {
      toast.success(t("تم الحفظ", "Saved"));
      setEditingId(null);
      loadUsers();
    } else {
      toast.error(t("تعذّر الحفظ: ", "Save failed: ") + (res.error || ""));
    }
  }

  async function removeUser(u: AdminUser) {
    if (users.length <= 1) { toast.error(t("لا يمكن حذف آخر مستخدم أدمن.", "Cannot delete the last remaining admin.")); return; }
    if (!confirm(t(`حذف المستخدم "${u.name}"؟`, `Delete user "${u.name}"?`))) return;
    const res = await adminDeleteUser(u.id);
    if (res.ok) {
      toast.success(t("تم الحذف", "Deleted"));
      logAudit("config_imported", `Admin user removed: ${u.email}`);
      loadUsers();
    } else {
      toast.error(t("تعذّر الحذف: ", "Delete failed: ") + (res.error || ""));
    }
  }

  return (
    <div className="kd-admin-settings">
      <form className="kd-admin-card" onSubmit={submitPassword}>
        <h3>{t("تغيير كلمة مروري", "Change My Password")}</h3>
        <p className="kd-admin-hint">{t("الحساب الحالي:", "Current account:")} <code>{myEmail || "—"}</code></p>
        <label><span>{t("كلمة المرور الحالية", "Current Password")}</span><input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} autoComplete="current-password" required /></label>
        <div className="kd-admin-grid-2">
          <label><span>{t("كلمة المرور الجديدة", "New Password")}</span><input type="password" value={next} onChange={(e) => setNext(e.target.value)} autoComplete="new-password" required /></label>
          <label><span>{t("تأكيد كلمة المرور", "Confirm Password")}</span><input type="password" value={confirmPwd} onChange={(e) => setConfirmPwd(e.target.value)} autoComplete="new-password" required /></label>
        </div>
        {pwMsg && <div className={`kd-admin-msg ${pwMsg.kind === "ok" ? "is-ok" : "is-err"}`}>{pwMsg.text}</div>}
        <button type="submit" className="kd-btn-primary kd-btn-gold" disabled={pwBusy}>{pwBusy ? t("جارٍ الحفظ...", "Saving...") : t("تحديث كلمة المرور", "Update Password")}</button>
      </form>

      <div className="kd-admin-card">
        <h3>{t("مستخدمو لوحة التحكم", "Dashboard Admin Users")}</h3>
        <p className="kd-admin-hint">
          {t("أضف زملاءك كأدمنز مستقلين، لكل واحد اسمه وبريده وكلمة مروره الخاصة. أي أدمن يمكنه إضافة أو حذف مستخدمين آخرين.", "Add colleagues as independent admins, each with their own name, email, and password. Any admin can add or remove other users.")}
        </p>
        <div className="kd-admin-toolbar">
          <button type="button" className="kd-btn-primary kd-btn-gold" onClick={() => setShowAdd((s) => !s)}>
            {showAdd ? t("إلغاء", "Cancel") : "+ " + t("إضافة مستخدم", "Add User")}
          </button>
          <span className="kd-admin-path-count">{t("عدد المستخدمين:", "Total users:")} <strong>{users.length}</strong></span>
        </div>

        {showAdd && (
          <form className="kd-admin-path-row" onSubmit={addUser} style={{ flexDirection: "column", alignItems: "stretch", gap: "0.75rem" }}>
            <div className="kd-admin-grid-2">
              <label><span>{t("الاسم", "Name")}</span><input type="text" value={newName} onChange={(e) => setNewName(e.target.value)} required /></label>
              <label><span>{t("البريد الإلكتروني", "Email")}</span><input type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} required /></label>
            </div>
            <label><span>{t("كلمة المرور", "Password")}</span><input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} autoComplete="new-password" required /></label>
            <button type="submit" className="kd-btn-primary" disabled={adding}>{adding ? t("جارٍ الإضافة...", "Adding...") : t("إضافة", "Add")}</button>
          </form>
        )}

        {loading ? (
          <p className="kd-admin-hint">{t("جارٍ التحميل...", "Loading...")}</p>
        ) : (
          <div className="kd-admin-paths-list">
            {users.map((u) => (
              <div className="kd-admin-path-row" key={u.id}>
                {editingId === u.id ? (
                  <div className="kd-admin-path-fields">
                    <div className="kd-admin-grid-2">
                      <label><span>{t("الاسم", "Name")}</span><input type="text" value={editName} onChange={(e) => setEditName(e.target.value)} /></label>
                      <label><span>{t("البريد الإلكتروني", "Email")}</span><input type="email" value={editEmail} onChange={(e) => setEditEmail(e.target.value)} /></label>
                    </div>
                  </div>
                ) : (
                  <div className="kd-admin-path-fields">
                    <strong>{u.name}</strong>
                    <span className="kd-admin-hint">{u.email}</span>
                  </div>
                )}
                <div className="kd-admin-path-actions">
                  {editingId === u.id ? (
                    <>
                      <button className="kd-admin-action-btn" onClick={() => saveEdit(u.id)} title={t("حفظ", "Save")}>✓</button>
                      <button className="kd-admin-action-btn" onClick={() => setEditingId(null)} title={t("إلغاء", "Cancel")}>✕</button>
                    </>
                  ) : (
                    <>
                      <button className="kd-admin-action-btn" onClick={() => startEdit(u)} title={t("تعديل", "Edit")}>✎</button>
                      <button className="kd-admin-action-btn kd-admin-action-danger" onClick={() => removeUser(u)} title={t("حذف", "Delete")}>✕</button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
